import { useIntl } from 'react-intl'
import * as Yup from 'yup'

export default function useValidationSchemaRenderer() {
  const intl = useIntl()

  const validSchemaOverview = Yup.object().shape({
    partnerTypeID: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    categoryIDs: Yup.array()
      .of(Yup.string())
      .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    contactName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    subCategoryIDs: Yup.array()
      .of(Yup.string())
      .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    contactSurname: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    contactEmail: Yup.string()
      .email(intl.formatMessage({id: 'GLOBAL.VALIDATION.EMAIL'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    contactPhone: Yup.string()
      .test({
        name: 'phoneNumber',
        message: intl.formatMessage({id: 'GLOBAL.VALIDATION.PHONE'}),
        test: (value) => {
          if (value) {
            const regex = /^[^\_]+$/
            return regex.test(value)
          }
          return true
        },
      })
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
  })

  const validationSchemaProfessionalDetails = Yup.object().shape({
    qualifications: Yup.string().max(
      1000,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000})
    ).nullable(),
    certificates: Yup.string().max(
      1000,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000})
    ).nullable(),
    conferencesAttended: Yup.string().max(
      1000,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000})
    ).nullable(),
    education: Yup.string().max(
      1000,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000})
    ).nullable(),
    experience: Yup.string().max(
      1000,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000})
    ).nullable(),
    memberships: Yup.string().max(
      1000,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000})
    ).nullable(),
  })

  const validationSchemaServiceDetails = Yup.object().shape({
    additionalInformations: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .test(
        'additionalInformations',
        intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}),
        (value) => {
          let htmlEl = document.createElement('div')
          if (value && htmlEl) {
            htmlEl.innerHTML = value
            const textValue = htmlEl.innerText.trim()

            if (textValue.length === 0) {
              return false
            }
          }

          return true
        }
      )
      .test(
        'additionalInformations',
        intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000}),
        (value) => {
          let htmlEl = document.createElement('div')
          if (value && htmlEl) {
            htmlEl.innerHTML = value
            const textValue = htmlEl.innerText.trim()

            if (textValue.length > 1000) {
              return false
            }
          }

          return true
        }
      ),
    infoContentData: Yup.array()
      .of(
        Yup.object().shape({
          data: Yup.string()
            .optional()
            .nullable()
            .test(
              'additionalInformations',
              intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000}),
              (value) => {
                let htmlEl = document.createElement('div')
                if (value && htmlEl) {
                  htmlEl.innerHTML = value
                  const textValue = htmlEl.innerText.trim()

                  if (textValue.length > 1000) {
                    return false
                  }
                }

                return true
              }
            ),
          id: Yup.string().optional().nullable(),
          name: Yup.string().optional().nullable(),
          contentURL: Yup.string().optional().nullable(),
        })
      )
      .optional()
      .nullable(),
    imagesData: Yup.array()
      .of(
        Yup.object().shape({
          imageUrl: Yup.string().required(
            intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
          ),
        })
      )
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .test('image', intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}), (value) => {
        if (value?.length === 0) {
          return false
        }

        return true
      }),
  })

  const validationSchemaSocialMedia = Yup.object().shape({})

  const validationSchemaContractDetails = Yup.object().shape({})

  const validationSchemaFinancialDetails = Yup.object().shape({
    financialOfficialName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialInvoiceAddress: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialBankName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialTaxOffice: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialIBAN: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialTaxNumber: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
  })

  const renderer = (editable: {
    overview: boolean
    professionalDetails: boolean
    serviceDetails: boolean
    socialMedia: boolean
    contractDetails: boolean
    financialDetails: boolean
  }) => {
    if (editable.overview) {
      return validSchemaOverview
    } else if (editable.professionalDetails) {
      return validationSchemaProfessionalDetails
    } else if (editable.serviceDetails) {
      return validationSchemaServiceDetails
    } else if (editable.socialMedia) {
      return validationSchemaSocialMedia
    } else if (editable.contractDetails) {
      return validationSchemaContractDetails
    } else if (editable.financialDetails) {
      return validationSchemaFinancialDetails
    }
    return Yup.object().shape({})
  }

  return {renderer}
}
